
import { useState, useEffect } from 'react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  setIsAlertOpen: (isOpen: boolean) => void;
}

export default function QuoteModal({ isOpen, onClose, setIsAlertOpen}: QuoteModalProps) {
  const [formData, setFormData] = useState({
    companyName: '',
    department: '',
    name: '',
    email: '',
    phone: '',
    paperInfo: '',
    paymentMethod: '請求書払い',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('department', formData.department);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('paperInfo', formData.paperInfo);
      formBody.append('paymentMethod', formData.paymentMethod);
      formBody.append('message', formData.message);

      const response = await fetch('/lp/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setIsAlertOpen(true);
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          department: '',
          name: '',
          email: '',
          phone: '',
          paperInfo: '',
          paymentMethod: '請求書払い',
          message: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto pt-8 pb-8">
      <div className="bg-white rounded-2xl max-w-2xl w-full relative">
        <button
          onClick={onClose}
          className="sticky top-4 float-right mr-4 mt-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        <div className="p-8 pt-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
              <i className="ri-file-list-3-line text-3xl text-blue-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">無料見積依頼</h2>
            <p className="text-gray-600">必要な情報をご入力ください</p>
          </div>

          <form id="quote-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                会社名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="株式会社○○"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                所属
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="営業部 / 研究開発部"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="03-1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                論文情報 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="paperInfo"
                required
                value={formData.paperInfo}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, paperInfo: e.target.value });
                  }
                }}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="論文のURL、タイトル、著者名、DOIなどをご記入ください"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.paperInfo.length}/500文字</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                決済方法
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="請求書払い">請求書払い</option>
                <option value="クレジット決済">クレジット決済</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                その他
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="その他ご要望がございましたらご記入ください"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-check-line text-2xl text-green-600"></i>
                <p className="text-green-700 font-medium">送信が完了しました！</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <p className="text-red-700 font-medium">送信に失敗しました。もう一度お試しください。</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  見積を依頼する
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}